import React, { useMemo, useState } from 'react'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { DataTable, type DataTableSorting } from '@workspace/ui/components/DataTable'
import { Tabs, TabList, TabPanel, Tab } from '@workspace/ui/components/Tabs'
import { Button } from '@workspace/ui/components/Button'
import { CheckCheck } from 'lucide-react'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { useGetNotificationList } from '@/shared/hooks/notifications/useGetNotificationList'
import { useNotificationColumns } from '@/shared/components/notifications/useNotificationColumns'
import { useToggleReadNotification, useMarkAllNotificationsRead } from '@/shared/hooks/use-notifications'
import { FilterDropdown } from '@/shared/components/FilterDropdown'
import { api } from '@/shared/lib/api'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { NotificationTypeEnum, type Notification, type NotificationCategory } from '@workspace/lib/api/notification'

const searchSchema = z.object({
    tab: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
    status: z.string().optional(),
    requesters: z.string().optional(),
})

export const Route = createFileRoute('/notifications/')({
    validateSearch: searchSchema,
    component: NotificationsPage,
})

function NotificationsPage() {
    const { t } = useTranslation()

    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const activeTab = (searchParams.tab as NotificationCategory) || 'general'
    const currentPage = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : { sortBy: 'createdAt', sortDirection: 'desc' }

    const setActiveTab = (tab: NotificationCategory) => updateSearch({ tab, page: 1 })
    const setCurrentPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    // Status Filter
    const statusValues = searchParams.status ? searchParams.status.split(',') : []
    const setStatusValues = (status: string[]) =>
        updateSearch({ status: status.length > 0 ? status.join(',') : undefined, page: 1 })
    const statusOptions = [
        { id: 'true', name: t('notification.status.read') },
        { id: 'false', name: t('notification.status.unread') },
    ]

    // Requester Filter
    const requesterValues = searchParams.requesters ? searchParams.requesters.split(',') : []
    const setRequesterValues = (requesters: string[]) =>
        updateSearch({ requesters: requesters.length > 0 ? requesters.join(',') : undefined, page: 1 })
    const [requesterSearchQuery, setRequesterSearchQuery] = useState('')
    const [isRequesterFilterOpen, setIsRequesterFilterOpen] = useState(false)
    const debouncedRequesterSearch = useDebounce(requesterSearchQuery, 300)

    const { data: usersData, isLoading: isLoadingRequesters } = useQuery({
        queryKey: ['users', debouncedRequesterSearch],
        queryFn: () => api.user.listAll({ page: 1, take: 100, q: debouncedRequesterSearch || undefined }),
        enabled: isRequesterFilterOpen,
    })

    const requesterOptions = [
        { id: 'system', name: 'Hệ thống' },
        ...(usersData?.data.map(user => ({ id: user.id, name: user.fullName })) || []),
    ]

    const params = useMemo(() => {
        const queryParams: any = {
            page: currentPage,
            take: pageSize,
        }
        if (sorting) {
            queryParams.order = sorting.sortDirection.toUpperCase()
            queryParams.orderBy = sorting.sortBy
        }
        if (requesterValues.length > 0) {
            queryParams.requesterIds = requesterValues
        }
        if (statusValues.length === 1) {
            if (statusValues[0] === 'true') {
                queryParams.isRead = true
            } else if (statusValues[0] === 'false') {
                queryParams.isRead = false
            }
        }
        return queryParams
    }, [currentPage, pageSize, sorting, requesterValues, statusValues])

    const { data, isLoading } = useGetNotificationList({
        category: activeTab,
        params,
    })

    const { mutate: toggleRead } = useToggleReadNotification()
    const { mutate: markAllRead } = useMarkAllNotificationsRead()

    const handleMarkAsRead = (id: string) => {
        toggleRead({ id, isRead: true })
    }

    const handleMarkAllAsRead = () => {
        markAllRead()
    }

    const handleTabChange = (key: React.Key) => {
        setActiveTab(key as NotificationCategory)
        setCurrentPage(1)
        setRequesterValues([])
        setStatusValues([])
    }

    const handleSortingChange = (newSorting: DataTableSorting | null) => {
        setSorting(newSorting)
        setCurrentPage(1)
    }

    const handlePageSizeChange = (size?: number) => {
        const newSize = size ?? 10
        setPageSize(newSize)
        setCurrentPage(1)
    }

    const router = useRouter()

    const taskType: string[] = [
        NotificationTypeEnum.TASK_ASSIGNED,
        NotificationTypeEnum.TASK_NEARDUE,
        NotificationTypeEnum.TASK_OVERDUE,
        NotificationTypeEnum.TASK_DEADLINE_CHANGED,
        NotificationTypeEnum.TASK_COMMENT,
        NotificationTypeEnum.NEW_COMMENT,
        NotificationTypeEnum.REQUEST_APPROVAL_REMINDER,
        NotificationTypeEnum.SUBORDINATE_PICKED_TASK_FROM_OTHER_UNIT,
    ]

    const approvalType: string[] = [
        NotificationTypeEnum.NEW_REQUEST_APPROVAL,
        NotificationTypeEnum.REQUEST_APPROVAL_REJECTED,
        NotificationTypeEnum.REQUEST_APPROVAL_APPROVED,
    ]

    const handleRowClick = (notification: Notification) => {
        if (!notification.isRead) {
            toggleRead({ id: notification.id, isRead: true })
        }

        if (taskType.includes(notification.type)) {
            return router.navigate({
                to: `/legal-documents/tasks`,
                search: {
                    searchId: notification.task?.id,
                    type: 'TASK',
                },
            })
        }

        if (approvalType.includes(notification.type)) {
            return router.navigate({
                to: `/legal-documents/approvals`,
                search: {
                    searchId: notification.metadata?.requestApprovalId,
                    type: 'APPROVAL',
                },
            })
        }

        if (notification.type === NotificationTypeEnum.ROLE_CHANGED) {
            return router.navigate({
                to: `/profile`,
            })
        }

        if (notification.type === NotificationTypeEnum.PROJECT_ITEM_CHANGED) {
            const projectId = notification.metadata?.projectId || notification.task?.projectId
            if (!projectId) return

            const itemType = notification.metadata?.itemType?.toLowerCase()
            const isTask = notification.task || itemType === 'task' || itemType === 'công việc'

            if (isTask) {
                // Chỉ lấy ID liên quan đến Task, tránh lấy documentId gây lỗi API 404
                const taskId = notification.task?.id || notification.metadata?.taskId || notification.metadata?.itemId

                return router.navigate({
                    to: `/projects/${projectId}/tasks`,
                    search: {
                        taskId,
                    },
                })
            } else {
                // Lấy ID liên quan đến Document (ưu tiên documentId, fallback sang itemId)
                const searchId = notification.metadata?.documentId || notification.metadata?.itemId

                return router.navigate({
                    to: `/projects/${projectId}/project-profile`,
                    search: {
                        searchId,
                    },
                })
            }
        }
    }

    const columns = useNotificationColumns(handleMarkAsRead)
    const notifications = data?.data || []
    const totalResults = data?.meta.itemCount || 0
    const totalPages = data?.meta.pageCount || 0

    return (
        <div className="flex flex-col gap-4 max-lg:gap-2 h-[calc(100vh-150px)] min-h-[500px] overflow-hidden">
            <div className="flex flex-wrap items-center justify-between shrink-0 gap-3">
                <h1 className="text-2xl font-bold">{t('notification.title')}</h1>
                <Button variant="outline" className="gap-2" onClick={handleMarkAllAsRead}>
                    <CheckCheck className="size-4!" />
                    {t('notification.markAllAsRead')}
                </Button>
            </div>

            <div className="flex flex-wrap items-center shrink-0 gap-4 max-lg:gap-2">
                <Tabs selectedKey={activeTab} onSelectionChange={handleTabChange}>
                    <TabList className="w-fit">
                        <Tab id="general">{t('notification.categories.all')}</Tab>
                        <Tab id="approval">{t('notification.categories.approval')}</Tab>
                        <Tab id="warning">{t('notification.categories.warning')}</Tab>
                    </TabList>
                </Tabs>
                <FilterDropdown
                    placeholder={t('notification.columns.sender')}
                    inputPlaceholder={t('notification.columns.sender')}
                    options={requesterOptions}
                    selectedValues={requesterValues}
                    onChange={values => {
                        setRequesterValues(values)
                        setCurrentPage(1)
                    }}
                    onSearchChange={setRequesterSearchQuery}
                    setOpen={setIsRequesterFilterOpen}
                    isLoading={isLoadingRequesters}
                />
                <FilterDropdown
                    placeholder={t('notification.columns.status')}
                    options={statusOptions}
                    selectedValues={statusValues}
                    onChange={values => {
                        setStatusValues(values)
                        setCurrentPage(1)
                    }}
                />
            </div>

            {/* We use a single DataTable to handle all tabs rendering to avoid unmounting/remounting state */}
            <div className="flex flex-col flex-1 min-h-0">
                <DataTable
                    data={notifications}
                    columns={columns}
                    enableRowSelection={false}
                    enableSorting={true}
                    sorting={sorting}
                    setSorting={handleSortingChange}
                    isLoading={isLoading}
                    emptyString={t('common.emptyState')}
                    containerClassName="flex-1 overflow-y-auto min-h-0"
                    onRowClick={handleRowClick}
                />
                <div className="shrink-0 mt-4">
                    <TableDataFooter
                        selectedCount={0}
                        onDelete={() => {}}
                        deleteDisabled={true}
                        totalCount={totalResults}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </div>
            </div>
        </div>
    )
}
